import React, { useState, useRef } from 'react';
import './index.scss';
import back from 'asset/img/back.png';
import { useNavigate } from 'react-router-dom';
import background from 'asset/img/bk-person.webp';
import white from 'asset/img/white.webp';
import avator from 'asset/img/avator.webp';
import { userLogout, uploadUserAvatar, userSave, getUserInfo } from 'network';
import { clearNativeAuthMirror } from 'utils/nativePreferences';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from 'actions';
import { message } from 'antd';
import { CameraOutlined, EditOutlined } from '@ant-design/icons';
import {
    isCapacitorIosNative,
    pickAvatarImageViaCapacitorCamera,
} from 'utils/capacitorIosAvatarCamera';
import { APP_VERSION } from 'constants/appVersion';
import { getOsVersionLabel } from 'utils/deviceOsVersion';

const Edit = () => {
    let navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const fileInputRef = useRef(null);
    const [avatarPreview, setAvatarPreview] = useState(user?.avatar || avator);
    const [uploading, setUploading] = useState(false);
    const [name, setName] = useState(user?.name || '');
    const [nameEditing, setNameEditing] = useState(false);

    // 当user变化时更新本地状态
    React.useEffect(() => {
        if (user?.avatar) {
            setAvatarPreview(user.avatar);
        }
        if (user?.name) {
            setName(user.name);
        }
    }, [user]);

    const handleBack = () => {
        navigate(`/personCenter`);
    };

    // 选择头像：iOS 原生包走系统相册/相机；其余环境仍为隐藏 file input（与改前一致）
    const handleAvatarClick = async () => {
        if (isCapacitorIosNative()) {
            try {
                const file = await pickAvatarImageViaCapacitorCamera();
                if (file) {
                    await runAvatarUploadFromFile(file);
                }
            } catch (err) {
                console.error(err);
                message.error(err?.message || '选取图片失败，请重试');
            }
            return;
        }
        fileInputRef.current?.click();
    };

    // 图片压缩函数
    const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    // 计算新尺寸（保持宽高比）
                    let width = img.width;
                    let height = img.height;
                    
                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = width * ratio;
                        height = height * ratio;
                    }
                    
                    // 创建canvas进行压缩
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    
                    // 设置图片质量（抗锯齿）
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    
                    // 绘制图片
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // 转换为Blob（JPEG格式，更小）
                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                // 创建新的File对象，保持原文件名
                                const compressedFile = new File([blob], file.name, {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                });
                                console.log(`图片压缩: ${(file.size / 1024).toFixed(2)}KB -> ${(blob.size / 1024).toFixed(2)}KB`);
                                resolve(compressedFile);
                            } else {
                                reject(new Error('图片压缩失败'));
                            }
                        },
                        'image/jpeg',
                        quality
                    );
                };
                img.onerror = () => reject(new Error('图片加载失败'));
                img.src = event.target.result;
            };
            reader.onerror = () => reject(new Error('文件读取失败'));
            reader.readAsDataURL(file);
        });
    };

    // 与 input file 选图共用：校验 → 压缩 → 预览 → 上传（上传接口与逻辑不变）
    const runAvatarUploadFromFile = async (file) => {
        if (!file.type.startsWith('image/')) {
            message.error('请选择图片文件');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            message.error('图片大小不能超过5MB');
            return;
        }

        try {
            setUploading(true);
            const compressedFile = await compressImage(file);

            const reader = new FileReader();
            reader.onload = (event) => {
                setAvatarPreview(event.target.result);
            };
            reader.readAsDataURL(compressedFile);

            await uploadAvatar(compressedFile);
        } catch (err) {
            console.error('图片压缩失败:', err);
            message.error('图片处理失败，请重试');
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await runAvatarUploadFromFile(file);
    };

    // 上传头像
    const uploadAvatar = async (file) => {
        const formData = new FormData();
        formData.append('avatar', file);

        try {
            const res = await uploadUserAvatar(formData);
            if (res?.code === 0) {
                message.success('头像上传成功');
                // 如果返回了签名URL，立即更新预览
                if (res?.data?.avatar_url) {
                    setAvatarPreview(res.data.avatar_url);
                }
                // 更新用户信息（会获取新的签名URL）
                await refreshUserInfo();
            } else {
                message.error(res?.msg || '上传失败');
                // 恢复原头像
                setAvatarPreview(user?.avatar || avator);
            }
        } catch (err) {
            console.error(err);
            message.error(err?.response?.data?.msg || err?.message || '上传失败');
            // 恢复原头像
            setAvatarPreview(user?.avatar || avator);
        } finally {
            setUploading(false);
            // 清空文件输入
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // 刷新用户信息
    const refreshUserInfo = async () => {
        try {
            const res = await getUserInfo({});
            if (res?.code === 0 && res?.data) {
                // 更新Redux store中的用户信息
                dispatch(setUser(res.data));
                setAvatarPreview(res.data.avatar || avator);
                setName(res.data.name || '');
            }
        } catch (err) {
            console.error('获取用户信息失败', err);
        }
    };

    // 保存昵称
    const handleSaveName = async () => {
        if (!name.trim() || name.trim() === user?.name) {
            setNameEditing(false);
            return;
        }

        if (name.trim().length < 2 || name.trim().length > 4) {
            message.error('昵称需要2～4个字');
            return;
        }

        try {
            const res = await userSave({ name: name.trim() });
            if (res?.code === 0) {
                message.success('昵称保存成功');
                setNameEditing(false);
                await refreshUserInfo();
            } else {
                message.error(res?.msg || '保存失败');
            }
        } catch (err) {
            console.error(err);
            message.error(err?.response?.data?.msg || err?.message || '保存失败');
        }
    };

    const exit = () => {
        userLogout({}).then(() => {
            try {
                localStorage.removeItem('_pwd');
            } catch (e) {
                /* ignore */
            }
            clearNativeAuthMirror();
            navigate('/login');
        });
    };

    const gotoDemoMenu = () => {
        // 演示彩蛋：进入 demo 菜单并在 Detail 中走隔离逻辑
        navigate(`/menu?from=yingshi&serial_id=985&demo=1&demo_serial_id=222`);
    };

    return (
        <div className='personCenterEdit-container'>
            <img src={background} className='white' alt="" />
            <img src={white} className='white' alt="" />
            <img src={back} className='personCenterEdit-back' onClick={handleBack} alt="返回" />
            <div className='personCenterEdit-content'>
                {/* 头像编辑 */}
                <div className='personCenterEdit-item avatar-item' onClick={handleAvatarClick}>
                    <div className='item-label'>头像</div>
                    <div className='avatar-wrapper'>
                        <div className='avatar-preview'>
                            <img src={avatarPreview} alt="头像" />
                            {uploading && <div className='uploading-overlay'>上传中...</div>}
                        </div>
                        <div className='avatar-edit-icon'>
                            <CameraOutlined />
                        </div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>

                {/* 昵称编辑 */}
                <div className={`personCenterEdit-item name-item ${nameEditing ? 'editing' : ''}`}>
                    <div className='item-label'>昵称</div>
                    <div className='name-wrapper'>
                        {nameEditing ? (
                            <div className='name-edit-wrapper'>
                                <input
                                    type="text"
                                    className='name-input'
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    onBlur={handleSaveName}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveName();
                                        }
                                    }}
                                    placeholder="请输入昵称（2～4个字）"
                                    autoFocus
                                    maxLength={4}
                                />
                            </div>
                        ) : (
                            <div className='item-value' onClick={() => setNameEditing(true)}>
                                {user?.name || '—'}
                            </div>
                        )}
                        {!nameEditing && (
                            <div className='name-edit-icon' onClick={() => setNameEditing(true)}>
                                <EditOutlined />
                            </div>
                        )}
                    </div>
                </div>

                {/* 手机号（只读） */}
                <div className='personCenterEdit-item'>
                    <div className='item-label'>手机号码</div>
                    <div className='item-value readonly'>{user?.mobile || '—'}</div>
                </div>

                {/* 应用版本（发版时改 constants/appVersion.js）；系统版本来自 UA */}
                <div className='personCenterEdit-item personCenterEdit-item-version'>
                    <div className='item-label'>应用版本</div>
                    <div className='item-value readonly'>v{APP_VERSION}</div>
                </div>
                <div className='personCenterEdit-item personCenterEdit-item-version'>
                    <div className='item-label'>系统版本</div>
                    <div className='item-value readonly'>{getOsVersionLabel()}</div>
                </div>

                <div className='personCenterEdit-exit' onClick={exit}>退出登录</div>
            </div>
            <div className='personCenterEdit-footer' onClick={gotoDemoMenu} role="button" tabIndex={0}>
                © 2026 悟空听说 版权所有
            </div>
        </div>
    );
};

export default Edit; 